import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listTransfers, revokeTransfer, getSession, createFileRequest, listReceived, claimReceived, storeLaunchCode, openReceived } from '../api';
import { logEvent } from '../utils/analytics';
import ShareWithColleague from '../components/ShareWithColleague';
import InvitedColleagues from '../components/InvitedColleagues';
import PlanStatus from '../components/PlanStatus';
import { useStoreFreeGb, freeGbCopy } from '../hooks/useStoreFreeGb';

// "Request files" — the reverse flow: ask another office to send YOU records.
// They get an email with a link that pre-fills you as the recipient.
function RequestFilesCard() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null); setState('sending');
    try {
      await createFileRequest({ counterpart_email: email, counterpart_name: name || undefined });
      setState('sent');
    } catch (e) {
      setErr(e.message); setState('idle');
    }
  };

  if (!open) {
    return (
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <strong>Need records from another office?</strong>{' '}
          <span className="muted">We'll email them a secure link with your address pre-filled.</span>
        </div>
        <button className="btn btn-secondary" onClick={() => setOpen(true)}>Request files</button>
      </div>
    );
  }
  if (state === 'sent') {
    return (
      <div className="card">
        <strong>Request sent ✓</strong>{' '}
        <span className="muted">{email} just received an email with a one-click secure upload link addressed to you.</span>{' '}
        <button className="btn btn-secondary" style={{ marginLeft: 8 }} onClick={() => { setOpen(false); setState('idle'); setEmail(''); setName(''); }}>Done</button>
      </div>
    );
  }
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Request files from an office</h2>
      <p className="muted">Patient details are never included in the email — share those with the office directly.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
        <input type="email" placeholder="frontdesk@otheroffice.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input type="text" placeholder="Office or doctor name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || state === 'sending'} onClick={submit}>
          {state === 'sending' ? 'Sending…' : 'Send request'}
        </button>
        <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

const STORE_URL = 'https://store.intake.dental/';

// "Open my Store" without a second email code: /me/store-launch-code mints a
// 60 s code the Store app's /launch page exchanges for a session. Any failure
// (Store dark, network, old API) falls back to the plain Store link — the
// sender still gets there, they just sign in again.
function OpenStoreButton({ source, children, className = 'btn btn-primary' }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    try { logEvent('store_keep_click', { source }); } catch { /* analytics optional */ }
    setBusy(true);
    try {
      const r = await storeLaunchCode();
      if (r?.success && r.code && r.url) {
        window.location.href = `${r.url}?code=${encodeURIComponent(r.code)}`;
        return;
      }
      window.location.href = STORE_URL;
    } catch {
      window.location.href = STORE_URL;
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className={className} onClick={go} disabled={busy}>
      {busy ? 'Opening…' : children}
    </button>
  );
}

// Packages other offices sent TO this sender's email. The API cannot mint a
// link to open one (the recipient token is stored hashed and the Store claim
// needs a PIN-gated download token), so "Open" explains the delivery email is
// the way in; "Save to my Store" keeps it without a token or PIN because the
// signed-in email IS the address it was sent to.
function ReceivedSection({ storeFreeGb }) {
  const [items, setItems] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState(null);
  // per-recipient_id: { state: 'saving' | 'saved' | 'error', text }
  const [claims, setClaims] = useState({});
  // per-recipient_id: { state: 'opening' | 'error', text }
  const [opens, setOpens] = useState({});
  const navigate = useNavigate();

  const load = async (next = null) => {
    try {
      if (next) setLoadingMore(true);
      const r = await listReceived({ limit: 25, cursor: next });
      const rows = r?.received || r?.items || [];
      setItems((prev) => (next && prev ? [...prev, ...rows] : rows));
      setCursor(r?.next_cursor || null);
    } catch (e) {
      // An API ahead of this endpoint (404) just means "nothing to show".
      if (e.status === 404) setItems([]); else setErr(e.message);
    } finally {
      setLoadingMore(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (row) => {
    const id = row.recipient_id;
    setClaims((c) => ({ ...c, [id]: { state: 'saving' } }));
    try {
      const r = await claimReceived(id);
      const n = r?.kept ?? 0;
      setClaims((c) => ({ ...c, [id]: { state: 'saved', text: n ? `Saved ${n} file${n === 1 ? '' : 's'} to your Store.` : 'Already in your Store.' } }));
      try { logEvent('store_claim_received', { source: 'send_dashboard' }); } catch { /* analytics optional */ }
    } catch (e) {
      let text = e.message || 'Could not save this package.';
      if (e.status === 409 || e.code === 'E2E_NOT_KEPT') text = 'This package is end-to-end encrypted with a PIN only you hold, so Store cannot file it for you. Open it from the delivery email and use "Keep in Store" there.';
      else if (e.status === 403 || e.code === 'NOT_RECIPIENT') text = 'This package was sent to a different email address. Sign in as that address to keep it.';
      else if (e.status === 410) text = 'This package has expired or was revoked, so there is nothing left to save.';
      else if (e.status === 404 && e.code === 'STORE_DISABLED') text = 'Store is not available yet.';
      setClaims((c) => ({ ...c, [id]: { state: 'error', text } }));
    }
  };

  // The delivery link cannot be rebuilt (its token is stored hashed), but the
  // addressee signed in to Send has already proved what the PIN proves: the
  // API mints a 1h download token for this recipient row and the package
  // page takes it from the fragment (never the query — fragments stay out
  // of server logs and GA).
  const openPkg = async (row) => {
    const id = row.recipient_id;
    setOpens((o) => ({ ...o, [id]: { state: 'opening' } }));
    try {
      const r = await openReceived(id);
      setOpens((o) => { const n = { ...o }; delete n[id]; return n; });
      try { logEvent('received_open', { source: 'send_dashboard', e2e: !!r?.e2e }); } catch { /* analytics optional */ }
      navigate(`/received/${encodeURIComponent(id)}#dt=${encodeURIComponent(r.dt)}`);
    } catch (e) {
      let text = e.message || 'Could not open this package.';
      if (e.status === 403 || e.data?.code === 'NOT_RECIPIENT') text = 'This package was sent to a different email address. Sign in as that address to open it.';
      else if (e.status === 410) text = 'This package has expired or was revoked.';
      else if (e.status === 404) text = 'This package is no longer listed.';
      setOpens((o) => ({ ...o, [id]: { state: 'error', text } }));
    }
  };

  if (items === null && !err) return null;
  if (err) return <div className="card"><strong>Received</strong> <span className="muted">— could not load: {err}</span></div>;
  if (!items.length) return null;

  const fromLine = (row) => {
    const who = row.sender?.display_name || row.sender?.email || 'Another office';
    const practice = row.sender?.practice_name;
    return practice && practice !== who ? `${who} · ${practice}` : who;
  };
  const expired = (row) => row.status === 'expired' || row.status === 'revoked' || row.status === 'deleted' || !!row.revoked_at || (row.expires_at && new Date(row.expires_at) < new Date());

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Received</h2>
      <p className="muted" style={{ marginTop: -4 }}>
        Files other offices sent to {getSession()?.user?.email || 'your email'}. Open any of them here, or keep them in your Store
        {storeFreeGb ? ` (${storeFreeGb} GB free)` : ''} — no PIN needed, because this is the address they were sent to.
      </p>
      <div style={{ display: 'grid', gap: 10 }}>
        {items.map((row) => {
          const c = claims[row.recipient_id];
          const o = opens[row.recipient_id];
          const gone = expired(row);
          return (
            <div key={row.recipient_id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <strong>
                  {row.source === 'referral' && <span className="badge badge-blue" style={{ marginRight: 6 }}>Referral</span>}
                  {row.source === 'lab_case' && <span className="badge badge-green" style={{ marginRight: 6 }}>Lab</span>}
                  {row.title || <span className="muted">(untitled)</span>}
                </strong>
                <span className="muted" style={{ fontSize: 12 }}>{new Date(row.created_at).toLocaleDateString()}</span>
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                From {fromLine(row)} · {row.file_count || 0} file{row.file_count === 1 ? '' : 's'}
                {row.expires_at && !gone && <> · expires {new Date(row.expires_at).toLocaleDateString()}</>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {gone && <span className="badge badge-gray">{row.status === 'revoked' || row.revoked_at ? 'Revoked' : 'Expired'}</span>}
                {row.viewed && <span className="badge badge-green">viewed</span>}
                {row.downloaded && <span className="badge badge-blue">downloaded</span>}
                {!row.viewed && !row.downloaded && !gone && <span className="badge badge-gray">not opened yet</span>}
              </div>
              {!gone && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                  {row.recipient_url
                    ? <a className="btn btn-secondary" href={row.recipient_url}>Open</a>
                    : <button type="button" className="btn btn-secondary" onClick={() => openPkg(row)} disabled={o?.state === 'opening'}>{o?.state === 'opening' ? 'Opening…' : 'Open'}</button>}
                  {c?.state !== 'saved' && (
                    <button type="button" className="btn btn-primary" onClick={() => save(row)} disabled={c?.state === 'saving'}>
                      {c?.state === 'saving' ? 'Saving…' : 'Save to my Store'}
                    </button>
                  )}
                </div>
              )}
              {o?.state === 'error' && <p className="error" style={{ fontSize: 13, margin: 0 }}>{o.text}</p>}
              {c?.state === 'saved' && <p style={{ fontSize: 13, margin: 0, color: '#047857' }}>✓ {c.text} <OpenStoreButton source="send_received" className="btn btn-secondary" >Open my Store →</OpenStoreButton></p>}
              {c?.state === 'error' && <p className="error" style={{ fontSize: 13, margin: 0 }}>{c.text}</p>}
            </div>
          );
        })}
      </div>
      {cursor && (
        <div style={{ marginTop: 10 }}>
          <button type="button" className="btn btn-secondary" onClick={() => load(cursor)} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'Show more'}</button>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const [transfers, setTransfers] = useState(null);
  const [err, setErr] = useState(null);
  const storeFreeGb = useStoreFreeGb();
  const session = getSession();
  const me = session?.user || null;

  const load = async () => {
    try {
      const r = await listTransfers();
      setTransfers(r.transfers || []);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { if (session?.token) load(); }, []);

  if (!session?.token) {
    return (
      <div className="page narrow">
        <div className="card">
          <h1>Sign in to view your transfers</h1>
          <Link className="btn btn-primary" to="/">Go to sign-in</Link>
        </div>
      </div>
    );
  }

  const onRevoke = async (id) => {
    if (!confirm('Revoke this transfer? Files will be permanently deleted and recipients will lose access immediately.')) return;
    try {
      await revokeTransfer(id, 'user-revoked');
      await load();
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="page">
      <h1>My transfers</h1>
      <p className="lead">All files you've sent through send.intake.dental.</p>

      <PlanStatus />

      {/* Send is a pipe: packages expire and the bytes are purged. Store is
          where they stay. Same sign-in, free allowance read from the API —
          the one upsell that costs a sender nothing to try. */}
      <div className="card" style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <strong>Your sent files expire.</strong>{' '}
            <span className="muted">Keep them in Store — encrypted, filed by patient, shareable again any time. {freeGbCopy(storeFreeGb, { suffix: 'free' }).replace(/^free to start$/, 'Free to start')}, same sign-in.</span>
          </div>
          <OpenStoreButton source="send_dashboard">Open my Store →</OpenStoreButton>
        </div>
      </div>

      <ReceivedSection storeFreeGb={storeFreeGb} />

      <RequestFilesCard />

      <ShareWithColleague />
      <InvitedColleagues />

      {err && <div className="error">{err}</div>}
      {!transfers && <p className="muted">Loading…</p>}
      {transfers && transfers.length === 0 && (
        <div className="card"><p className="muted">No transfers yet. <Link to="/">Send your first one →</Link></p></div>
      )}

      {transfers && transfers.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table>
            <thead>
              <tr>
                <th>Created</th>
                <th>Subject</th>
                <th>Recipients</th>
                <th>Status</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((t) => (
                <tr key={t.id}>
                  <td>{new Date(t.created_at).toLocaleDateString()}</td>
                  <td>
                    {t.source === 'referral' && <span className="badge badge-blue" style={{ marginRight: 6 }}>Referral</span>}
                    {t.source === 'lab_case' && <span className="badge badge-green" style={{ marginRight: 6 }}>Lab</span>}
                    {t.title || <span className="muted">(untitled)</span>}
                  </td>
                  <td>
                    {(t.secure_transfer_recipients || []).map(r => (
                      <div key={r.recipient_email} style={{ fontSize: 12 }}>
                        {r.recipient_email}
                        {r.view_count > 0 && <span className="badge badge-green" style={{ marginLeft: 6 }}>viewed</span>}
                        {r.download_count > 0 && <span className="badge badge-blue" style={{ marginLeft: 4 }}>downloaded</span>}
                      </div>
                    ))}
                  </td>
                  <td><StatusBadge status={t.status} /></td>
                  <td>{new Date(t.expires_at).toLocaleDateString()}</td>
                  <td>
                    {/* A colleague's referral / lab package shows here too, but only its sender can revoke it. */}
                    {!['revoked', 'deleted', 'expired'].includes(t.status) && (!me?.id || t.sender_user_id === me.id) && (
                      <button className="btn btn-danger" onClick={() => onRevoke(t.id)}>Revoke</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:    ['badge-gray',  'Pending'],
    ready:      ['badge-blue',  'Delivered'],
    viewed:     ['badge-green', 'Viewed'],
    downloaded: ['badge-green', 'Downloaded'],
    revoked:    ['badge-red',   'Revoked'],
    deleted:    ['badge-red',   'Deleted'],
    expired:    ['badge-gray',  'Expired']
  };
  const [cls, label] = map[status] || ['badge-gray', status];
  return <span className={`badge ${cls}`}>{label}</span>;
}
